import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import Fuse, { IFuseOptions } from 'fuse.js'

import { getOlName } from '../../../../views/ols/center/utils/functions'
import { Center } from '../center/center'
import { CenterEventAction } from '../center-event-action/center-event-action'
import { Enrollment } from '../enrollment/enrollment'
import { Ol } from '../ol/ol'
import { User } from '../users/user'

export class CenterEventProperties {
  /**
   *  Get the Name of the student full Name
   * @param student
   *
   */
  static getStudentName(student: Pick<Enrollment, 'childFirstName' | 'childLastName'>): string {
    return toFullName(student.childFirstName, student.childLastName)
  }

  /**
   *  Search for the studentId and return the full name of the student
   * @param studentId
   *
   */
  static searchStudentName(
    studentId: number,
    enrollments: Pick<Enrollment, 'id' | 'childFirstName' | 'childLastName'>[]
  ): string {
    const student = enrollments.find((enrollment) => enrollment.id === studentId)
    if (!student) return '-'
    return this.getStudentName(student)
  }

  /**
   *  Search for the userId and return the full name of the user
   * @param userId
   *
   */
  static searchUserName(
    userId: number,
    users: Pick<User, 'id' | 'firstName' | 'lastName'>[]
  ): string {
    const user = users.find((user) => user.id === userId)
    if (!user) return '-'
    return toFullName(user.firstName, user.lastName)
  }

  /**
   *  Search for the olId and return the full name of the ol
   * @param olId
   *
   */
  static searchOlName(
    userId: number,
    ols: Pick<Ol, 'id' | 'mainContactFirstName' | 'mainContactLastName'>[]
  ): string {
    const ol = ols.find((ol) => ol.id === userId)
    return getOlName(ol)
  }

  /**
   * Get the center event dates
   * It will return the start date and end date of the center event
   * - Start date is the **minimum** departure date of the center event
   * - End date is the **maximum** return date of the center event
   * @param center - The center object containing journey data
   * @returns An object with startDate and endDate
   */
  static getCenterDateRange(center?: Pick<Center, 'centerToJourneys'>): {
    startDate?: Date
    endDate?: Date
  } {
    if (!center) return { startDate: undefined, endDate: undefined }
    return (
      center?.centerToJourneys?.reduce(
        (acc, { journey: { date } }) => {
          if (!acc.startDate || new Date(date.departureDate) < acc.startDate) {
            acc.startDate = new Date(date.departureDate)
          }
          if (!acc.endDate || new Date(date.returnDate) > acc.endDate)
            acc.endDate = new Date(date.returnDate)
          return acc
        },
        { startDate: undefined, endDate: undefined } as { startDate?: Date; endDate?: Date }
      ) ?? { startDate: undefined, endDate: undefined }
    )
  }

  static professionColorBasedOnKeywords(
    aaKeywords: string[],
    profession?: string
  ): TailwindColor | undefined {
    const options: IFuseOptions<string> = {
      threshold: 0.21,
      isCaseSensitive: false
    }
    const fuse = new Fuse(aaKeywords, options)
    const result = fuse.search(profession ?? '')
    if (result.length > 0) {
      // Return the first result which has the highest score (lowest numerical value)
      return tailwindColors.primary.DEFAULT
    }
    // If no match is found, return undefined
    if (!profession) return undefined
  }

  static getActionCreationInformation(action: CenterEventAction): string {
    const createdByName = valueRender(action.createdBy, (value) =>
      toFullName(value.firstName, value.lastName)
    )

    const createdAt = DateFormatter.ddMMYYYY_HHMM(action.createdAt)

    return `${createdByName} - ${createdAt}`
  }

  /**
   * Gets the time elapsed from the given last activity date until now.
   */
  static getInactivityDuration(
    lastActivityAt: Date | string,
    formatDays: (total: number) => string = (total) => `${total}J`
  ): string {
    // Ensure that lastActionDate is a valid Date object
    let lastActionDate = lastActivityAt

    // If it's a string, convert it to a Date object
    if (typeof lastActionDate === 'string') {
      lastActionDate = new Date(lastActionDate)
    }

    // If it's not a valid Date object, return a default value or handle the error
    if (!(lastActionDate instanceof Date) || isNaN(lastActionDate.getTime())) {
      throw new Error('Invalid date provided')
    }

    const now = new Date()
    const diff = now.getTime() - lastActionDate.getTime()
    const seconds = diff / 1000
    const minutes = seconds / 60
    const hours = minutes / 60
    const days = hours / 24

    if (days >= 1) {
      return formatDays(Math.floor(days))
    } else if (hours >= 1) {
      return `${Math.floor(hours)}H`
    } else if (minutes >= 1) {
      return `${Math.floor(minutes)}m`
    } else {
      return `${Math.floor(seconds)}s`
    }
  }
}
